import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import _ from 'lodash';
import objectSupport from 'dayjs/plugin/objectSupport';
import randomString from 'random-string';

import PropTypes from 'prop-types';

import {
    ProviderHeader,
    GoBackButton,
    Tag,
    Text,
    ModeratorCard,
    SessionDetailCard,
    MemberCard,
    CheckInCard,
    RulesCard,
    ShareLinkCard,
    PDPADialog,
    ContainedButton,
} from '../../components/index';

import EditSessionDialog from '../../components/Provider/Dialog/EditSessionDialog';

import {
    providerPageWidth,
    userRole,
    SESSION_COLORS,
    SESSION_TYPE,
    GROUP_RULES_TH,
    OPEN_RULES_TH,
} from '../../constant';

import { GET_OTHER_SESSION_DETAIL } from '../../services/provider';
import { canJoin } from '../../services/patient';

import { createRoom, joinRoom } from '../../services';

import { alertError } from '../../redux/stateActions';

import socket from '../../socket';

dayjs.extend(objectSupport);

const STATUS = Object.freeze({
    WAITING: 'WAITING',
    ON_CALLING: 'ON_CALLING',
    SUCCESS: 'SUCCESS',
});

const OtherOpenSessionDetail = ({ history, location }) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(STATUS.WAITING);

    const handleGoBack = () => {
        if (location.state?.prevPath) {
            const path = {
                pathname: location.state.prevPath,
                state: {
                    tab: location.state.prevTab,
                },
            };
            history.push(path);
        } else {
            history.goBack();
        }
    };

    const handleGoHome = () => {
        history.push('/provider');
    };

    /*--------------- Get Open Session ---------------*/
    const [sessionData, setSessionData] = useState({});
    const [rules, setRules] = useState([]);

    const { loading: getSessionLoading, refetch: getSessionRefetch } = useQuery(
        GET_OTHER_SESSION_DETAIL,
        {
            variables: { id: id },
            skip: _.isEmpty(user) || !id,
            pollInterval: 30000,
            onError: (err) => {
                console.log('GET_OTHER_SESSION_DETAIL onError', err);
                dispatch(alertError(`Cannot get Session data: ${err.message}`));
            },
            onCompleted: (res) => {
                console.log('GET_OTHER_SESSION_DETAIL onCompleted', res);

                if (_.isEmpty(res.session)) {
                    handleGoHome();
                    return;
                }

                setSessionData(res.session);

                const { meet, actualEndTime, roomId, sessionEnd, cancelled } = res.session;

                setRules(OPEN_RULES_TH.filter((r) => meet.rules.includes(r.id)));

                // check if session cancelled or ended
                if (cancelled) {
                    dispatch(alertError(`Session Cancelled`));
                    handleGoHome();
                    return;
                }

                // check if videos call has end
                if (actualEndTime && sessionEnd) {
                    return setStatus(STATUS.SUCCESS);
                }

                if (roomId) {
                    return setStatus(STATUS.ON_CALLING);
                }

                return setStatus(STATUS.WAITING);
            },
        }
    );

    /*--------------- PDPA ---------------*/
    const [openPDPA, setOpenPDPA] = useState(false);
    const [accepted, setAccepted] = useState(false);

    const onCheckChange = (e) => {
        setAccepted(e.target.checked);
    };

    const handleClosePDPA = () => {
        setOpenPDPA(false);
        setAccepted(false);
    };

    const onAccept = () => {
        setOpenPDPA(false);

        handleJoinRoom();
    };

    const handleJoinRoom = async () => {
        if (!accepted) {
            setOpenPDPA(true);
            return;
        }

        setLoading(true);

        try {
            const canJoinRes = await canJoin({
                sessionId: sessionData.id,
            });

            if (canJoinRes.data) {
                if (canJoinRes.data.canJoin === false) {
                    setLoading(false);
                    return dispatch(alertError(`session is full.`));
                }
            } else {
                throw new Error(canJoinRes);
            }

            const params = new URLSearchParams();
            params.append('roomId', sessionData.roomId);
            params.append('sessionId', randomString({ length: 16 }).toLowerCase());
            params.append('role', userRole.PATIENT);
            params.append(
                'displayName',
                `${user.fullname}|${user.patient?.id}|${user.avatar?.url}|Patient`
            );
            params.append('password', '');

            const joinRes = await joinRoom(sessionData.roomId, params);

            window.location.href = `${window.location.origin}/provider/other-opencall/${sessionData.id}?roomId=${joinRes.data.roomId}&session=${joinRes.data.session}`;
        } catch (error) {
            console.log('group open ', error);
            dispatch(alertError(`Cannot join open session: ${error.response?.data?.message}`));
            setLoading(false);
        }
    };

    useEffect(() => {
        // fire after first render. for open session
        socket.emit('joinOpenSession', { sessionId: id });

        // fire after provider finished set up open room
        socket.on('providerSetUpOpenRoom', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });

        socket.on('sessionEnd', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });
    }, []);

    return (
        <div
            style={{
                padding: '15px 20px 50px',
                maxWidth: providerPageWidth,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '15px',
                position: 'relative',
            }}
        >
            <PDPADialog
                open={openPDPA}
                onClose={handleClosePDPA}
                accepted={accepted}
                onCheckChange={onCheckChange}
                onAccept={onAccept}
            />
            <ProviderHeader>
                <GoBackButton $text onClick={handleGoBack} />
            </ProviderHeader>

            {sessionData?.meet?.type === 'open' &&
                sessionData?.meet?.sessionType === 'public' &&
                !_.isEmpty(sessionData) && (
                    <ShareLinkCard
                        url={`${window.location.origin}/appointment/open/${sessionData?.id}`}
                    />
                )}

            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}>
                <SessionDetailCard data={sessionData?.meet} showStaff>
                    <div>
                        {(status === STATUS.WAITING || status === STATUS.ON_CALLING) && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginTop: '10px',
                                }}
                            >
                                <ContainedButton
                                    onClick={handleJoinRoom}
                                    loading={loading}
                                    disabled={_.isEmpty(sessionData) || status === STATUS.WAITING}
                                    style={{
                                        width: '300px',
                                    }}
                                >
                                    Join Now
                                </ContainedButton>
                            </div>
                        )}
                    </div>
                </SessionDetailCard>
                {!_.isEmpty(rules) && <RulesCard data={rules} />}
            </div>
        </div>
    );
};

OtherOpenSessionDetail.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default OtherOpenSessionDetail;
